import * as urlUtil from 'js/utils/url.util';
import * as dateUtil from 'js/utils/date.util';
import { getCookie } from 'js/utils/cookie.util';
import { getAffiliateData } from 'js/utils/affiliate.util';

import * as Types from './types';
import * as server from './server';
import * as constants from './constants';
import { IBannerSlide } from '../../types';

export const getSliderOptions: Types.IGetSliderOptions = ({ bannerFeed, formFactor }) => {
    const feedConfiguration = bannerFeed.bannerFeedConfiguration[formFactor];

    return {
        height: `${feedConfiguration.bannerHeight}${feedConfiguration.bannerHeightUnits}`,
        animation: {
            displayTime: Number(feedConfiguration.slideAnimationTime) || 0,
            type: feedConfiguration.slideAnimationType || 'fade'
        },
        controls: {
            arrows: {
                enabled: feedConfiguration.isArrowsControlsEnabled,
                position: `${feedConfiguration.arrowsControlsPosition || '0'}${feedConfiguration.arrowsControlsUnits}`
            },
            bullets: {
                enabled: feedConfiguration.isBulletsControlsEnabled,
                position: {
                    offsetX: Number(feedConfiguration.bulletsControlsXOffset) || 0,
                    offsetY: Number(feedConfiguration.bulletsControlsYOffset) || 0,
                    x: feedConfiguration.bulletsControlsXPosition || 'center',
                    y: feedConfiguration.bulletsControlsYPosition || 'bottom'
                }
            },
            progressBar: {
                enabled: feedConfiguration.isProgressBarControlsEnabled,
                position: feedConfiguration.progressBarControlsPosition
            },
            buttons: {
                enabled: false
            }
        }
    };
};

export const getSliderPlayerTags: Types.IGetSliderPlayerTags = async ({ bannerFeed, isLoggedIn }) => {
    const playerTags: string[] = [];
    const feedContent = bannerFeed.bannerFeedContent || [];

    if (!feedContent.length || !isLoggedIn) {
        return [];
    }

    feedContent.forEach((slideGroup) => {
        slideGroup.children.forEach((slide) => {
            const segmentationSettings = slide.isCustomSegmentationEnabled
                ? slide.loggedInSegmentation
                : slideGroup.loggedInSegmentation;

            if (!segmentationSettings || !segmentationSettings.length) {
                return;
            }

            segmentationSettings.forEach((segmentationGroup) =>
                segmentationGroup.forEach((segmentationItem) =>
                    segmentationItem.segmentationItemSettings.forEach(({ conditionName }) => {
                        if (segmentationItem.segmentationType === constants.SEGMENTATION_TYPES.playerTag) {
                            if (!conditionName || playerTags.includes(conditionName)) {
                                return;
                            }

                            playerTags.push(conditionName);
                        }
                    })
                )
            );
        });
    });

    if (playerTags.length) {
        const response = await server.getPlayerTags(playerTags.join(','));

        return response?.data?.tags || [];
    }

    return [];
};

export const isSlideShownByActive: Types.IIsSlideShownByActive = ({ slide, slideGroup, formFactor }) => {
    return slideGroup.isActive && slide[formFactor].isActive;
};

export const checkSchedulerItemSettings: Types.ICheckSchedulerItemSettings = ({
    schedulerItemSetting,
    schedulerType,
    schedulerTimeZone
}) => {
    const { conditionDuration, conditionType } = schedulerItemSetting;
    const isUTCTimezone = schedulerTimeZone === constants.SCHEDULER_CONDITION_TIMEZONES.server;

    switch (schedulerType) {
        case constants.SCHEDULER_TYPES.date: {
            const currentDate = Date.now();
            const startTime = isUTCTimezone
                ? Date.parse(conditionDuration.split(' ')[0])
                : Date.parse(conditionDuration.split(' ')[0].slice(0, -1));

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.before) {
                return currentDate < startTime;
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.after) {
                return currentDate >= startTime;
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.between) {
                const endTime = isUTCTimezone
                    ? Date.parse(conditionDuration.split(' ')[1])
                    : Date.parse(conditionDuration.split(' ')[1].slice(0, -1));

                return startTime <= currentDate && currentDate < endTime;
            }

            return true;
        }
        case constants.SCHEDULER_TYPES.timeOfTheDay: {
            const currentDate = new Date();
            const startTime = conditionDuration.split(' ')[0];
            const endTime = conditionDuration.split(' ')[1];

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.before) {
                return (
                    `${dateUtil.getFullHours(currentDate, isUTCTimezone)}:${dateUtil.getFullMinutes(
                        currentDate,
                        isUTCTimezone
                    )}` < startTime
                );
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.after) {
                return (
                    `${dateUtil.getFullHours(currentDate, isUTCTimezone)}:${dateUtil.getFullMinutes(
                        currentDate,
                        isUTCTimezone
                    )}` >= startTime
                );
            }

            if (conditionType === constants.SCHEDULER_CONDITION_TYPES.between) {
                const currentTime = `${dateUtil.getFullHours(currentDate, isUTCTimezone)}:${dateUtil.getFullMinutes(
                    currentDate,
                    isUTCTimezone
                )}`;

                return startTime <= currentTime && currentTime < endTime;
            }

            return true;
        }
        case constants.SCHEDULER_TYPES.dayOfTheWeek: {
            const currentDay = isUTCTimezone ? new Date().getUTCDay() : new Date().getDay();
            const targetDay = Number(conditionDuration);

            return currentDay === targetDay;
        }
        default:
            return true;
    }
};

const isSlideShownByScheduler: Types.IIsSlideShownByScheduler = ({ slide, slideGroup }) => {
    const schedulerSettings = slide.isCustomSchedulerEnabled ? slide.scheduler : slideGroup.scheduler;
    const schedulerTimeZone = slide.isCustomSchedulerEnabled ? slide.schedulerTimeZone : slideGroup.schedulerTimeZone;

    if (!schedulerSettings || !schedulerSettings.length) {
        return true;
    }

    return schedulerSettings.some((schedulerGroup) =>
        schedulerGroup.every((schedulerItem) =>
            schedulerItem.schedulerItemSettings.some((schedulerItemSetting) =>
                checkSchedulerItemSettings({
                    schedulerTimeZone,
                    schedulerItemSetting,
                    schedulerType: schedulerItem.schedulerType
                })
            )
        )
    );
};

export const isSlideShownByLogin: Types.IIsSlideShownByLogin = ({ slide, slideGroup, isLoggedIn }) => {
    const isEnabledForGuest = slide.isCustomSegmentationEnabled ? slide.isEnabledForGuest : slideGroup.isEnabledForGuest;
    const isEnabledForLoggedIn = slide.isCustomSegmentationEnabled ? slide.isEnabledForLoggedIn : slideGroup.isEnabledForLoggedIn;

    return isLoggedIn ? isEnabledForLoggedIn : isEnabledForGuest;
};

export const checkURLParameter: Types.ICheckURLParameter = ({ conditionName, conditionValue, conditionType }) => {
    if (!conditionName) {
        return true;
    }

    const param = urlUtil.getLocationParam(conditionName);

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return urlUtil.isParamPresent(conditionName) && conditionValue === (param || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return urlUtil.isParamPresent(conditionName) && conditionValue !== (param || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.exist) {
        return urlUtil.isParamPresent(conditionName);
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.notExist) {
        return !urlUtil.isParamPresent(conditionName);
    }

    return true;
};

export const checkURLTemplate: Types.ICheckURLTemplate = ({
    isLoggedIn,
    conditionType,
    conditionValue,
    segmentationURLTemplates = []
}) => {
    if (!conditionValue || !isLoggedIn) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return segmentationURLTemplates.includes(conditionValue);
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return !segmentationURLTemplates.includes(conditionValue);
    }

    return true;
};

export const checkCountry: Types.ICheckCountry = ({ conditionValue, conditionType, userCountry }) => {
    if (!conditionValue || !userCountry) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === userCountry.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== userCountry.toLowerCase();
    }

    return true;
};

export const checkBrowser: Types.ICheckBrowser = ({ conditionValue, conditionType, browserName }) => {
    if (!conditionValue || !browserName) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === browserName.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== browserName.toLowerCase();
    }

    return true;
};

export const checkOS: Types.ICheckOS = ({ conditionValue, conditionType, os }) => {
    if (!conditionValue || !os) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === os.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== os.toLowerCase();
    }

    return true;
};

export const checkLanguage: Types.ICheckLanguage = ({ conditionValue, conditionType, userLanguage }) => {
    if (!conditionValue || !userLanguage) {
        return true;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return conditionValue.toLowerCase() === userLanguage.toLowerCase();
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return conditionValue.toLowerCase() !== userLanguage.toLowerCase();
    }

    return true;
};

export const checkCookie: Types.ICheckCookie = ({ conditionName, conditionValue, conditionType }) => {
    if (!conditionName) {
        return true;
    }

    const cookie = getCookie(conditionName);

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return cookie !== undefined && conditionValue === (cookie || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return cookie !== undefined && conditionValue !== (cookie || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.exist) {
        return cookie !== undefined;
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.notExist) {
        return cookie === undefined;
    }

    return true;
};

export const checkAffiliate: Types.ICheckAffiliate = ({ conditionName, conditionValue, conditionType }) => {
    if (!conditionName) {
        return true;
    }

    const cookie = getCookie(conditionName);
    const { advertiser } = getAffiliateData(conditionName) as { advertiser?: string };

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return cookie !== undefined && conditionValue === (advertiser || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return cookie !== undefined && conditionValue !== (advertiser || '');
    }

    return true;
};

export const checkPlayerTag: Types.ICheckPlayerTag = ({ conditionName, conditionValue, conditionType, playerTags }) => {
    if (!conditionName) {
        return true;
    }

    const playerTag = playerTags.find((tag) => tag.name === conditionName);

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.is) {
        return Boolean(playerTag) && conditionValue === (playerTag?.value || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.isNot) {
        return Boolean(playerTag) && conditionValue !== (playerTag?.value || '');
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.exist) {
        return Boolean(playerTag);
    }

    if (conditionType === constants.SEGMENTATION_CONDITION_TYPES.notExist) {
        return !playerTag;
    }

    return true;
};

export const checkSegmentationItemSettings: Types.ICheckSegmentationItemSettings = ({
    segmentationItemSetting,
    segmentationType,
    options
}) => {
    const { segmentationURLTemplates, userCountry, browserName, os, userLanguage, isLoggedIn, playerTags } = options;
    const { conditionValue, conditionName, conditionType } = segmentationItemSetting;

    switch (segmentationType) {
        case constants.SEGMENTATION_TYPES.URLParameter: {
            return checkURLParameter({ conditionValue, conditionName, conditionType });
        }
        case constants.SEGMENTATION_TYPES.URLTemplate: {
            return checkURLTemplate({ conditionValue, conditionType, segmentationURLTemplates, isLoggedIn });
        }
        case constants.SEGMENTATION_TYPES.country: {
            return checkCountry({ conditionValue, conditionType, userCountry });
        }
        case constants.SEGMENTATION_TYPES.browser: {
            return checkBrowser({ conditionValue, conditionType, browserName });
        }
        case constants.SEGMENTATION_TYPES.os: {
            return checkOS({ conditionValue, conditionType, os });
        }
        case constants.SEGMENTATION_TYPES.language: {
            return checkLanguage({ conditionValue, conditionType, userLanguage });
        }
        case constants.SEGMENTATION_TYPES.cookie: {
            return checkCookie({ conditionValue, conditionName, conditionType });
        }
        case constants.SEGMENTATION_TYPES.affiliate: {
            return checkAffiliate({ conditionValue, conditionName, conditionType });
        }
        case constants.SEGMENTATION_TYPES.playerTag: {
            return checkPlayerTag({ conditionValue, conditionName, conditionType, playerTags });
        }
        default:
            return true;
    }
};

export const isSlideShownBySegmentation: Types.IIsSlideShownBySegmentation = ({ slide, slideGroup, options }) => {
    const segmentationSettings = slide.isCustomSegmentationEnabled
        ? slide[options.isLoggedIn ? 'loggedInSegmentation' : 'guestSegmentation']
        : slideGroup[options.isLoggedIn ? 'loggedInSegmentation' : 'guestSegmentation'];

    if (!segmentationSettings || !segmentationSettings.length) {
        return true;
    }

    return segmentationSettings.some((segmentationGroup) =>
        segmentationGroup.every((segmentationItem) =>
            segmentationItem.segmentationItemSettings.some((segmentationItemSetting) =>
                checkSegmentationItemSettings({
                    options,
                    segmentationItemSetting,
                    segmentationType: segmentationItem.segmentationType
                })
            )
        )
    );
};

export const getSlides: Types.IGetSlides = ({ bannerFeed, options }) => {
    const slides: IBannerSlide[] = [];
    const { isLoggedIn, formFactor } = options;
    const feedContent = bannerFeed.bannerFeedContent || [];

    if (!feedContent.length) {
        return slides;
    }

    feedContent.forEach((slideGroup) => {
        slideGroup.children.forEach((slide) => {
            if (
                isSlideShownByActive({ slide, slideGroup, formFactor }) &&
                isSlideShownByScheduler({ slide, slideGroup }) &&
                isSlideShownByLogin({ slide, slideGroup, isLoggedIn }) &&
                isSlideShownBySegmentation({ slide, slideGroup, options })
            ) {
                slides.push({
                    id: slide.id,
                    name: slide.name,
                    ...slide[formFactor]
                });
            }
        });
    });

    return slides;
};
